import { useMemo } from 'react';
import { OptimusProvider } from '@mews-commander/ui';
import { LocalizedText, lt } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import { PaymentPlan } from '@mews-commander/data-access';
import { TableHeader, useTableState } from '@optimus-web/core';
import { TableDivider } from '../TableDivider';
import { PaymentTable } from '../PaymentTable';
import { CreatePaymentPlanAction } from '../../forms/CreatePaymentPlan/CreatePaymentPlanAction';
import { getTableEmptyState } from '../../common/utils';
import { useGetPaymentPlans } from './queries';
import { getPaymentPlansTableMapping } from './paymentPlansTableMapping';

export const PaymentPlansTable = ({ accountId, hideDivider = false }: { hideDivider?: boolean } & WithAccountId) => {
    const [tableState, setTableState] = useTableState<PaymentPlan>({
        pagination: {
            show: false,
        },
    });
    const { data, isFetching, isError, refetch } = useGetPaymentPlans({
        customerId: accountId,
    });

    const paymentPlanRecords = data?.paymentPlans ?? [];
    const columns = useMemo(() => getPaymentPlansTableMapping({ accountId }), [accountId]);

    return (
        <>
            <TableHeader
                title={<LocalizedText textKey={TextKey.PaymentPlansTableHeaderTitle} />}
                subTitle={<LocalizedText textKey={TextKey.PaymentPlansTableHeaderSubTitle} />}
                rightSide={<CreatePaymentPlanAction accountId={accountId} />}
            />
            <OptimusProvider density="spacious">
                <PaymentTable
                    columns={paymentPlanRecords.length === 0 ? [] : columns}
                    data={paymentPlanRecords}
                    uniqueRowId="id"
                    state={tableState}
                    onStateChange={setTableState}
                    amountOfRows={paymentPlanRecords.length}
                    loading={isFetching}
                    isError={isError}
                    onTryAgain={async () => refetch()}
                    emptyState={getTableEmptyState(
                        lt('PaymentPlansTableEmptyStatePrimaryText'),
                        lt('PaymentPlansTableEmptyStateSecondaryTextText'),
                        <CreatePaymentPlanAction accountId={accountId} />
                    )}
                />
            </OptimusProvider>
            {!hideDivider && <TableDivider />}
        </>
    );
};
